<template>
  <div class="md:mx-14 mx-4 relative">
    <div>
      <div v-if="isLoading" class="mt-10">
        <payment-link-details-loader />
      </div>
      <main class="mt-10" v-else>
        <!-- top -->
        <div>
          <div class="pr-5 mt-4 flex items-center">
            <p
              class="rounded-lg flex items-center px-2 py-1 border cursor-pointer shadow-sm mr-3"
              @click="goBack"
            >
              <back-arrow />
              <span class="pl-2 text-sm text-fg-muted font-light"> Back </span>
            </p>
            <el-breadcrumb
              separator-class="el-icon-arrow-right"
              class="text-fg-muted font-light"
            >
              <el-breadcrumb-item>Payment Link</el-breadcrumb-item>
              <el-breadcrumb-item>
                {{ currentPaymentLinkDetails.title }}
              </el-breadcrumb-item>
            </el-breadcrumb>
          </div>
          <div class="md:flex mt-7 mb-6 py-1 pb-3 justify-between items-center">
            <div class="md:flex items-center">
              <div class="hide-icon pr-3">
                <payment-details-icon size="h-6 w-6" />
              </div>
              <div class="md:pl-4">
                <div class="flex items-center pb-2.5">
                  <p class="capitalize pr-3 text-fg-default font-bold">
                    {{ currentPaymentLinkDetails.title }}
                  </p>
                  <p
                    class="rounded-xl text-sm bg-fg-subtle text-fg-default font-normal shadow-sm px-2 py-1"
                    :class="statusClass(currentPaymentLinkDetails.type)"
                  >
                    {{ formatServerName(currentPaymentLinkDetails.type) }}
                  </p>
                </div>
                <div class="flex items-center">
                  <p
                    class="border py-2 text-sm font-normal rounded-lg px-2 w-2/4 truncate shadow-sm mr-2"
                  >
                    {{ currentPaymentLinkDetails.url }}
                  </p>
                  <p
                    class="mr-2 cursor-pointer"
                    v-clipboard:copy="currentPaymentLinkDetails.url"
                    v-clipboard:success="onCopy"
                    v-clipboard:error="onError"
                  >
                    <copy-icon />
                  </p>
                  <!-- <p class="cursor-pointer">
                    <eye-icon />
                  </p> -->
                </div>
              </div>
            </div>

            <div class="flex pt-4 md:pt-0">
              <button
                v-if="isPaymentLinkValid"
                class="flex items-center gap-x-1 bg-surface border-fg-muted rounded-md px-3 py-3 text-fg-default"
                @click="showEditPaymentLink"
                :disabled="!hasDepositAccess"
              >
                <edit-pen-icon-2 />
                <span class="font-medium text-xs md:text-sm px-2.5">Edit</span>
              </button>
            </div>
          </div>
          <hr />
          <!-- description -->
          <div class="grid md:grid-cols-8 grid-cols-4 py-4 mt-8">
            <div class="col-span-2 pb-3 md:pb-0">
              <p class="text-sm text-fg-muted font-thin pb-2">Amount</p>
              <p>
                {{ currentPaymentLinkDetails.amount.symbol }}
                {{ addComma(currentPaymentLinkDetails.amount.amount) }}
              </p>
            </div>
            <div class="col-span-2">
              <p class="text-sm text-fg-muted font-thin pb-2">Total Earnings</p>
              <p>
                {{ currentPaymentLinkDetails.amount.symbol
                }}{{ addComma(totalAmountEarned) }}
              </p>
            </div>
            <div class="col-span-2 pb-3 md:pb-0">
              <p class="text-sm text-fg-muted font-thin pb-2">Status</p>
              <div
                :class="
                  !isPaymentLinkValid ? 'cursor-not-allowed opacity-40' : ''
                "
                class="flex items-center"
              >
                <el-switch
                  :disabled="!isPaymentLinkValid"
                  @change="updatePaymentLinkStatus"
                  v-model="linkStatus"
                ></el-switch>
                <span class="font-semibold capitalize pl-2">{{
                  currentPaymentLinkDetails.status
                }}</span>
              </div>
            </div>
            <div class="col-span-2">
              <p class="text-sm text-fg-muted font-thin pb-2">Date created</p>
              <p class="text-fg-muted font-thin">
                {{ tooltipDateContent(currentPaymentLinkDetails.createdAt) }}
              </p>
            </div>
          </div>
          <div class="mt-5 text-sm font-thin text-fg-muted">
            <p class="pb-1.5">Description</p>
            <p>{{ currentPaymentLinkDetails.description }}</p>
          </div>
        </div>
        <div>
          <el-tabs v-model="activeTab" @tab-click="handleClick" class="mt-5">
            <el-tab-pane label="Transactions" name="first">
              <payment-link-transaction-table
                isLoading="isLoading"
                :tableData="currentPaymentLinkTransactions"
                @sort="sortHandler"
              />
            </el-tab-pane>
            <el-tab-pane label="Link Activities" name="second">
              <payment-link-activities />
            </el-tab-pane>
          </el-tabs>
        </div>
      </main>
      <!-- dialogs -->
      <payment-link-created
        v-if="showPaymentLinkCreated"
        :showDialog="showPaymentLinkCreated"
        view="shareLink"
        @closeDialog="closeDialog('showPaymentLinkCreated')"
      />
      <edit-payment-link-drawer
        v-if="showEditPaymentLinkDrawer"
        :showDrawer="showEditPaymentLinkDrawer"
        @closeDrawer="closeDrawer('showEditPaymentLinkDrawer')"
        @getPaymentLinkDetails="refreshPaymentLinkDetails"
      />
      <el-dialog
        :show-close="false"
        title="Warning"
        :visible.sync="statusUpdateDialogVisible"
        width="43vh"
        center
      >
        <span
          >Are you sure you want to {{ paymentLinkStatus }} this payment
          link?</span
        >
        <span slot="footer" class="dialog-footer">
          <el-button
            class="hover:bg-white hover:text-primary"
            @click="cancelConfirm"
            >Cancel</el-button
          >
          <el-button
            class="bg-primary"
            type="primary"
            @click="togglePaymentLinkHandler"
            >Confirm</el-button
          >
        </span>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import { AddComma, FormatServerName, ParseLocalTime } from '@/utils/helpers.js'
import statusClass from '@/mixins/app.js'
import getPaymentLinkTransactions from '@/mixins/paymentLink.js'
import userKinds from '@/mixins/userKinds.js'

export default {
  mixins: [statusClass, getPaymentLinkTransactions, userKinds],
  components: {
    PaymentDetailsIcon: () =>
      import('@/assets/v2/icons/PaymentDetailsIcon.vue'),
    EditPenIcon2: () => import('@/assets/icons/EditPenIcon2.vue'),
    // EyeIcon: () => import('@/assets/v2/icons/EyeIcon2.vue'),
    PaymentLinkTransactionTable: () =>
      import(
        '@/components/v2/paymentLink/tables/PaymentLinkTransactionTable.vue'
      ),
    CopyIcon: () => import('@/assets/v2/icons/CopyIcon2.vue'),
    BackArrow: () => import('@/assets/icons/BackArrow.vue'),
    EditPaymentLinkDrawer: () =>
      import('@/components/v2/paymentLink/drawers/EditPaymentLinkDrawer.vue'),
    PaymentLinkActivities: () =>
      import('@/components/v2/paymentLink/PaymentLinkActivities.vue'),
    PaymentLinkDetailsLoader: () =>
      import('@/components/v2/skeletalLoaders/PaymentLinkDetailsLoader.vue')
  },
  data () {
    return {
      isLoading: false,
      showPaymentLinkCreated: false,
      showEditPaymentLinkDrawer: false,
      activeTab: 'first',
      status: this.$STATUS.ACTIVE,
      linkStatus: true,
      statusUpdateDialogVisible: false,
      payload: {
        page: 1,
        size: 5,
        status: '',
        type: '',
        from: '',
        to: '',
        sort_direction_desc: true,
        sort_by: 'updated_at'
      }
    }
  },
  methods: {
    parseLocalTime (val) {
      return ParseLocalTime(val)
    },
    cancelConfirm () {
      this.linkStatus = !this.linkStatus
      this.statusUpdateDialogVisible = false
    },
    updatePaymentLinkStatus () {
      this.statusUpdateDialogVisible = true
    },
    sortHandler () {
      this.payload.sort_direction_desc = !this.payload.sort_direction_desc
      this.getPaymentLinkDetails(this.currentPaymentLinkId)
      this.getPaymentLinkTransactions()
    },
    async refreshPaymentLinkDetails () {
      await this.getPaymentLinkDetails(this.currentPaymentLinkId)
      await this.getPaymentLinkTransactions()
    },
    togglePaymentLinkHandler () {
      this.statusUpdateDialogVisible = false
      this.isLoading = true
      const vm = this
      this.$store
        .dispatch(
          'paymentLink/togglePaymentLinkStatus',
          this.currentPaymentLinkId
        )
        .then(() => {
          this.isLoading = false
          this.getPaymentLinkDetails(this.currentPaymentLinkId)
          this.$swal({
            icon: 'success',
            title: `${this.isPaymentLinkActive ? 'Disabled' : 'Enabled'}`,
            text: `This payment link has been ${this.paymentLinkStatus}d`,
            showConfirmButton: false,
            iconColor: this.$primaryColor,
            allowOutsideClick: false
          })
          setTimeout(() => {
            this.$router.go()
          }, 2000)
        })
        .catch((error) => {
          this.isLoading = false
          const err = error.response ? error.response.data : error
          vm.$swal({
            icon: 'error',
            title: 'Oops',
            text: err,
            iconColor: this.$primaryColor,
            allowOutsideClick: false
          })
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    showEditPaymentLink () {
      this.showEditPaymentLinkDrawer = true
    },
    closeDrawer () {
      this.showEditPaymentLinkDrawer = false
    },
    handleClick () {},
    closeDialog (dialog) {
      this[dialog] = false
    },
    showDialog (dialog, arg = '') {
      if (arg) {
        this.transactionDetails = arg
      }
      if (dialog === 'showPaymentLinkCreated') {
        this.$store.dispatch(
          'paymentLink/setCreatedPaymentLinkDetails',
          this.currentPaymentLinkDetails
        )
      }
      this[dialog] = true
    },
    addComma (val) {
      return AddComma(val.toString())
    },
    formatServerName (val) {
      return FormatServerName(val)
    },
    getPaymentLinkDetails (id) {
      this.isLoading = true
      this.$store
        .dispatch('paymentLink/getPaymentLinkDetails', id)
        .then(() => {
          this.isLoading = false
          this.linkStatus =
            this.currentPaymentLinkDetails.status === this.$STATUS.ACTIVE
        })
        .catch((error) => {
          this.handleErrorResponseNotification(error)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    edit () {
      this.$router.push({
        path: '/payment-link',
        query: {
          mode: 'edit'
        }
      })
    },
    shareLink () {},
    onCopy: function (e) {
      this.$notify.success({
        title: 'Payment Link Copied'
      })
    },
    onError: function (e) {
      this.$notify.error('Error copying to clipboard!')
    },
    goBack () {
      this.$router.go(-1)
    }
  },
  computed: {
    totalAmountEarned () {
      return this.$store.state.paymentLink.paymentLinkTransactions.attributes
        .totalAmountEarned
    },
    isFeeBearerBusiness () {
      return this.currentPaymentLinkDetails.feeBearer === 'business'
    },
    isOneTimePayment () {
      return this.currentPaymentLinkDetails.type === 'one_time'
    },
    currentPaymentLinkId () {
      return this.$route.params.id
    },
    currentPaymentLinkDetails () {
      return this.$store.state.paymentLink.currentPaymentLinkDetails
    },
    currentPaymentLinkTransactions () {
      return this.$store.state.paymentLink.paymentLinkTransactions.items
    },
    isPaymentLinkActive () {
      return this.currentPaymentLinkDetails.status === this.$STATUS.ACTIVE
    },
    isPaymentLinkDisable () {
      return this.currentPaymentLinkDetails.status === this.$STATUS.INACTIVE
    },
    isPaymentLinkValid () {
      return this.currentPaymentLinkDetails.isValid
    },
    paymentLinkStatus () {
      return this.isPaymentLinkActive ? 'disable' : 'enable'
    }
  },
  async created () {
    await this.refreshPaymentLinkDetails()
  }
}
</script>

<style scoped>
article,
aside {
  box-shadow:
    0px 0px 1px rgba(3, 2, 7, 0.1),
    0px 8px 16px rgba(3, 2, 7, 0.1);
  border-radius: 15px;
}

>>> .el-switch.is-checked .el-switch__core {
  border-color: var(--color-primary);
  background-color: var(--color-primary);
}

>>> .el-switch.is-checked .el-switch__core {
  border-color: var(--color-primary);
  background-color: var(--color-primary);
}

>>> .el-tabs__item.is-active {
  color: var(--color-primary);
}

.el-tabs__active-bar {
  background: #f26722;
}

>>> .el-tabs__item:hover {
  color: var(--color-primary);
}

>>> .el-switch.is-disabled .el-switch__core {
  cursor: pointer;
}

>>> .el-tabs__active-bar.is-top {
  background: var(--color-primary);
}

>>> .el-switch__core {
  background: var(--foreground-on-disabled);
  border-color: var(--foreground-on-disabled);
  width: 48px;
  border-radius: 24px;
  height: 24.5px;
}

>>> .el-switch.is-checked .el-switch__core {
  background: var(--color-primary);
  border-color: var(--color-primary);
}

>>> .el-switch__core:after {
  width: 20px;
  height: 20px;
}

>>> .el-switch.is-checked .el-switch__core::after {
  margin-left: -20px;
}

button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

@media only screen and (max-width: 800px) {
  .hide-icon {
    display: none;
  }
}
</style>
